/**
 * Shared effective-booking-status helper — systemweites Farbschema.
 *
 * User-verordneter Status-Farbcode (Feb 2026):
 *   GRAU (#808080) = Angefragt und noch nicht zu einem Concierge zugeteilt
 *   BLAU (#1F4B7A) = Zugeteilt / In Bearbeitung / In Betreuung
 *   GRÜN (#4B7A55) = Erledigt (alle Aufgaben der Checkliste abgehakt)
 *   ROT  (#B02A37) = Fällig / Überfällig (heute noch nicht erledigt oder
 *                    Zeitraum bereits verstrichen ohne Abschluss)
 *
 * Root cause of previous sync bugs: the home-screen bar computed a *derived*
 * status (`in_service` when today ∈ [from, to]), whereas the detail modal
 * read the *raw* `booking.status` from the database. Those two values
 * disagreed when a booking was `confirmed` but the absence window was
 * already active. This helper is the single source for label + colour so
 * every UI surface (Team-Kalender, Kunden-Dashboard, Listen, Modals) stays
 * in perfect sync.
 */
export const STATUS_COLORS = {
  grey:  "#808080",  // GREY
  blue:  "#1F4B7A",  // BLUE
  green: "#4B7A55",  // GREEN
  red:   "#B02A37",  // RED
};

/**
 * Feb 2026 · Weissblom-Markenfarben (nicht Status). Wer eine Farbe aus dieser
 * Palette in einer Komponente braucht, importiert sie hier — kein weiterer
 * hardcoded Hex-Wert im Codebase. Die Farbpalette (Status + Brand) wird
 * ausschließlich in diesem File gepflegt.
 */
export const BRAND_COLORS = {
  navy:       "#0F1E3D",  // Hauptmarke Navy — Hintergründe, dunkler Text
  navyDark:   "#0A1428",  // Dunkler Modal-Backdrop-Ton (Navy heruntergezogen)
  ivory:      "#F7F3EC",  // Warme Elfenbein-Fläche · sekundäre Hintergründe
  gold:       "#C9A961",  // Weissblom-Gold · Markenakzent, Highlights
  goldDark:   "#8A6A2A",  // Dunklerer Gold-Ton für Text auf hellem Grund
  greenDark:  "#3A5D42",  // Dunklerer Grün-Ton — Text-Kontrast auf grünen Tints
  greenDeep:  "#2E5D4E",  // Dunkler Grün-Ton für Angebot-/Projekt-Akzente
  white:      "#FFFFFF",  // Reines Weiß — Kontrast auf farbigem Grund
};

export const BOOKING_PILL = {
  // Angefragt · Kunde hat Anfrage geschickt, Team hat NICHT bestätigt
  requested:   { label: "Angefragt",    label_en: "Requested",   bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  // Zugeteilt / In Bearbeitung / In Betreuung (alle blau)
  confirmed:   { label: "Bestätigt",    label_en: "Confirmed",   bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  accepted:    { label: "Bestätigt",    label_en: "Confirmed",   bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  in_service:  { label: "In Betreuung", label_en: "In service",  bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  in_progress: { label: "In Betreuung", label_en: "In service",  bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  // Erledigt · alle Punkte abgehakt · backend flipped
  completed:   { label: "Erledigt",     label_en: "Completed",   bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  done:        { label: "Erledigt",     label_en: "Completed",   bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  closed:      { label: "Erledigt",     label_en: "Completed",   bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  // Fällig / Überfällig · Zeitraum verstrichen, aber nicht erledigt
  overdue:     { label: "Überfällig",   label_en: "Overdue",     bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
  // Storniert · ROT (system-weite Storniert-Farbe · matches Team-Kalender)
  cancelled:   { label: "Storniert",    label_en: "Cancelled",   bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Compute the effective booking status. Order of precedence:
 *   1. `cancelled` — customer released the slot. Wins over everything.
 *   2. `completed` — team ticked off every checklist item (DB terminal).
 *   3. `overdue`   — heute > to_date, weder completed noch cancelled →
 *                    ROT (Concierge muss aufholen).
 *   4. `in_service` — heute ∈ [from_date, to_date] und DB nicht terminal.
 *   5. `requested`  — raw DB fallback (Angefragt-Grau).
 *
 * Mirrors backend `_all_booking_items_done` semantics for the `completed`
 * transition. Never invents states out of thin air.
 */
export function effectiveBookingStatus(booking) {
  if (!booking) return null;
  const raw = booking.status || null;
  if (raw === "cancelled") return "cancelled";
  if (raw === "completed") return "completed";
  const today = todayISO();
  const past = booking.to_date && today > booking.to_date;
  if (past) return "overdue";  // ROT — Zeitraum vorbei, aber nicht fertig
  const active = booking.from_date && booking.to_date &&
                 today >= booking.from_date && today <= booking.to_date;
  if (active) return "in_service";
  return raw || "requested";
}

export function bookingPill(booking) {
  const st = effectiveBookingStatus(booking);
  const pill = BOOKING_PILL[st] || { label: st || "—", bg: STATUS_COLORS.grey, fg: "#0F1E3D" };
  // Feb 2026 · Zusatzregel · Grau→Blau nur wenn ein Concierge zugewiesen ist.
  // Ohne Verantwortlichen (weder `booking.assigned_to` noch
  // `booking.assigned_concierge_id`) kann eine Buchung nicht „in Betreuung"
  // sein — der Pill bleibt Grau/„Angefragt", auch wenn das Datum bereits
  // aktiv ist. Sobald ein Admin einen Concierge zuteilt, springt die
  // Farbe automatisch auf Blau.
  const hasAssignee = !!(booking?.assigned_to || booking?.assigned_concierge_id);
  if (pill.bg === STATUS_COLORS.blue && !hasAssignee) {
    return { label: "Angefragt", bg: STATUS_COLORS.grey, fg: "#0F1E3D" };
  }
  return pill;
}

/**
 * Feb 2026 · Systemweite Aufgaben-Status-Farbe für Icon-Backgrounds und
 * Text auf farbigem Grund. Einzige Truth-Source — jede Task-Liste
 * (Anfragen, Recent Activity, Team-Requests, Projekte …) importiert diesen
 * Helper, damit Pills, Icon-Kreise und Karten-Akzente exakt denselben
 * Farbcode zeigen und keine Kategorie- oder Priorität-Farben mehr
 * abweichende Backgrounds erzeugen.
 *
 * Mapping-Regel: jeder Task-Status kollabiert auf genau eine der vier
 * Systemfarben (Grau / Blau / Grün / Rot). Storniert → Grau (Archiv).
 * Fällt ein Status durch das Raster, greift Grau als sichere Standard-
 * Zuweisung — nie eine Fremdfarbe.
 */
export const TASK_STATUS_COLOR = {
  // GRAU — Angefragt / Neu / noch nicht zugeteilt / storniert (Archiv)
  new:                 { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  received:            { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  requested:           { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  awaiting_approval:   { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  pending:             { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  cancelled:           { bg: STATUS_COLORS.grey,  fg: "#0F1E3D" },
  // BLAU — Zugeteilt / In Bearbeitung / In Betreuung
  under_review:        { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  quote_required:      { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  scheduled:           { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  in_progress:         { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  in_service:          { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  confirmed:           { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  accepted:            { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  active:              { bg: STATUS_COLORS.blue,  fg: "#F7F3EC" },
  // GRÜN — Erledigt / Geschlossen
  done:                { bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  completed:           { bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  closed:              { bg: STATUS_COLORS.green, fg: "#F7F3EC" },
  // ROT — Problem · Verzögerung · Fällig · Dringend
  overdue:             { bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
  urgent:              { bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
  problem:             { bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
  attention:           { bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
  emergency:           { bg: STATUS_COLORS.red,   fg: "#F7F3EC" },
};

/**
 * `taskStatusColor(status, opts?)` — kanonischer Helper für alle Aufgaben-
 * Listen. Sonderregel: `opts.urgent === true` überstimmt jeden anderen
 * Status auf ROT (Dringend gewinnt vor In-Bearbeitung / Erledigt) —
 * damit z. B. eine dringende offene Anfrage sofort rot signalisiert wird.
 */
export function taskStatusColor(status, opts = {}) {
  if (opts.urgent) return { bg: STATUS_COLORS.red, fg: "#F7F3EC" };
  return TASK_STATUS_COLOR[status] || { bg: STATUS_COLORS.grey, fg: "#0F1E3D" };
}

/**
 * `withAlpha(color, alpha)` — kanonischer Helper für halbtransparente Farben.
 * Nimmt einen 6-stelligen Hex-Wert (z. B. `STATUS_COLORS.red`) und einen
 * Alpha-Anteil (0.0-1.0) und liefert einen `rgba(r,g,b,a)`-String zurück.
 * Damit lebt JEDE Farbnuance in der App an einer einzigen Quelle: die 4
 * Systemfarben + 5 Markenfarben. Kein manuelles `rgba(15,30,61,0.08)`
 * mehr im Codebase — alle Tints leiten sich aus den zentralen Konstanten
 * ab und passen sich automatisch an, wenn eine Basis-Farbe justiert wird.
 */
/**
 * 24 Jun 2026 · Kanonische Umbrella-Labels je Systemfarbe (Design Guide).
 * Detail-Labels je Bereich bleiben erhalten; diese dienen als Fallback und
 * für Tooltips/Legenden.
 */
export const STATUS_KEY_LABEL = {
  grey:  "Angefragt",
  blue:  "Geplant",
  green: "Erledigt",
  red:   "Handlungsbedarf",
};

/** Reverse-Map: System-Hex → colorKey (grey/blue/green/red). */
export function hexToStatusKey(hex) {
  if (hex === STATUS_COLORS.red) return "red";
  if (hex === STATUS_COLORS.blue) return "blue";
  if (hex === STATUS_COLORS.green) return "green";
  return "grey";
}

/**
 * colorKey einer Buchung/Abwesenheit (mirror `bookingPill`).
 *
 * 24 Jun 2026 · ROT-Regel MUSS 1:1 dem Backend-Helfer
 * `_red_booking_ids` (team_dashboard_router.py) entsprechen:
 *   ROT = Status ∉ {cancelled, completed} UND `to_date < heute`.
 * `effectiveBookingStatus` liefert genau dann `overdue` (→ ROT).
 */
export function bookingColorKey(booking) {
  return hexToStatusKey(bookingPill(booking).bg);
}

/**
 * colorKey einer Anfrage/eines Sonderwunsches.
 *  · Dringende offene Anfrage → ROT (Handlungsbedarf gewinnt).
 *  · Grau → Blau, sobald ein Concierge zugewiesen ist (analog Buchungen).
 *  · Storniert/erledigt bleiben grau/grün.
 *
 * 24 Jun 2026 · ROT-Regel MUSS 1:1 dem Backend-Helfer
 * `_red_request_ids` entsprechen:
 *   ROT = `priority === "urgent"` UND Status ∉ {completed, cancelled}.
 */
export function requestColorKey(r) {
  const open = !["completed", "cancelled"].includes(r?.status);
  if (r?.priority === "urgent" && open) return "red";
  let key = hexToStatusKey(taskStatusColor(r?.status).bg);
  if (key === "grey" && open && r?.assigned_to) key = "blue";
  return key;
}

/**
 * colorKey eines Projekts — zentrale Single Source of Truth (24 Jun 2026,
 * zuvor lokal in `TeamProjects.jsx`).
 *
 *   GRAU  = In Planung / Pausiert / nicht zugeteilt / storniert (Archiv)
 *   BLAU  = In Bearbeitung mit zugewiesenem Concierge
 *   GRÜN  = Erledigt
 *   ROT   = Überfällig
 *
 * ROT-Regel MUSS 1:1 dem Backend-Helfer `_red_project_ids` entsprechen:
 *   ROT = Status ∉ {completed, cancelled} UND `end_date` gesetzt UND
 *         `end_date < heute`.
 */
export function projectColorKey(p) {
  const raw = p?.status || "planning";
  if (raw === "cancelled") return "grey";
  if (raw === "completed") return "green";
  const today = todayISO();
  if (p?.end_date && today > p.end_date) return "red";
  if (raw === "in_progress" && p?.assigned_to) return "blue";
  return "grey";
}

export function withAlpha(hex, alpha = 1) {
  if (!hex || typeof hex !== "string") return `rgba(0,0,0,${alpha})`;
  const h = hex.replace("#", "");
  const full = h.length === 3
    ? h.split("").map((c) => c + c).join("")
    : h;
  const num = parseInt(full, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
